import 'moment-timezone';
import moment from "moment"
import "moment/locale/pt-br"
import CompanyModel, { ICompanyModel } from "./CompanyModel";
import { GetValue, IgnoreTimeZoneApiDate } from "../../Helpers/helper";
import UserCompanyPermissionModel, { IUserCompanyPermissionModel } from "./UserCompanyPermissionModel";

export interface IUserModel {
    Id: number;
    Email?: string;
    Name?: string;
    PictureUrl?: string;
    Created: Date;
    CreatedById: number;
    Modified: Date;
    Login?: string;
    IsActive: boolean;
    IsMaster: boolean;
    VersionDate: Date;
    /**
     * Permissions' dictionary of user where key is company id (string) and value is the permission, until now only one permission per user.
     */
    Permissions?: Map<string, IUserCompanyPermissionModel>;

    //#region Setters

    SetID: (id: number) => IUserModel;
    SetEmail: (name: string) => IUserModel;
    SetName: (name: string) => IUserModel;
    SetPictureUrl: (value: string) => IUserModel;
    SetCreated: (createdAt: Date) => IUserModel;
    SetCreatedById: (id: number) => IUserModel;
    SetModified: (modifiedAt: Date) => IUserModel;
    SetLogin: (name: string) => IUserModel;
    SetIsActive: (isActive: boolean) => IUserModel;
    SetIsMaster: (isActive: boolean) => IUserModel;
    SetVersionDate: (value: Date) => IUserModel;
    SetPermission: (value?: Map<string, IUserCompanyPermissionModel>) => IUserModel

    //#endregion

    Clone: () => IUserModel;

    LoadFromApiByCompany: (response: any) => IUserModel;
    LoadFromAPI: (response: any) => IUserModel;
    LoadFromCached: (model: any) => IUserModel;
    GetToken: () => string | undefined;
    LoadPermissions: () => void;
    LoadPremissionsFromCollection: (permissions: IUserCompanyPermissionModel[]) => IUserModel;
    ToJson: () => string;
}

export default class UserModel implements IUserModel {

    Id!: number;
    Email?: string | undefined;
    Name?: string | undefined;
    PictureUrl?: string | undefined;
    Created!: Date;
    CreatedById!: number;
    Modified!: Date;
    Login?: string | undefined;
    IsActive!: boolean;
    IsMaster!: boolean;
    VersionDate!: Date;
    Permissions?: Map<string, IUserCompanyPermissionModel> | undefined;
    // Permissions: IUserCompanyPermissionModel[] | undefined;

    private _companies?: ICompanyModel[] | undefined;
    private _userCompanyPermissions?: IUserCompanyPermissionModel[] | undefined;
    private _token?: string;

    constructor() {
        this.Id = this.CreatedById = 0;
        this.Email = this.Name = this.PictureUrl = this.Login = "";
        this.Created = this.Modified = this.VersionDate = moment().toDate();
        this.IsActive = true;
        this.IsMaster = false;
        this._companies = [];
        this._userCompanyPermissions = [];
        this.LoadPermissions();
    }

    //#region Setters

    SetCreated = (createdAt: Date): IUserModel => {
        this.Created = createdAt!;
        return this;
    }

    SetCreatedById = (id: number): IUserModel => {
        this.CreatedById = id!;
        return this;
    }

    SetID = (id: number): IUserModel => {
        this.Id = id!
        return this;
    }

    SetIsActive = (isActive: boolean): IUserModel => {
        this.IsActive = isActive!;
        return this;
    }

    SetModified = (modifiedAt: Date): IUserModel => {
        this.Modified = modifiedAt!;
        return this;
    }

    SetName = (name: string): IUserModel => {
        this.Name = name!.trim();
        return this;
    }

    SetEmail = (email: string): IUserModel => {
        this.Email = email!.trim();
        return this;
    }

    SetLogin = (login: string): IUserModel => {
        this.Login = login!.trim();
        return this;
    }

    SetIsMaster = (isMaster: boolean): IUserModel => {
        this.IsMaster = isMaster!;
        return this;
    }

    SetPermission = (value?: Map<string, IUserCompanyPermissionModel>): IUserModel => {
        this.Permissions = value
        return this;
    }

    SetPictureUrl = (value: string): IUserModel => {
        this.PictureUrl = value!;
        return this;
    }

    SetVersionDate = (value: Date): IUserModel => {
        this.VersionDate = value!
        return this;
    }

    //#endregion

    Clone = (): IUserModel => {
        const result = new UserModel()
            .SetCreated(this.Created)
            .SetCreatedById(this.CreatedById)
            .SetEmail(this.Email!)
            .SetID(this.Id)
            .SetIsActive(this.IsActive)
            .SetIsMaster(this.IsMaster)
            .SetLogin(this.Login!)
            .SetModified(this.Modified)
            .SetName(this.Name!)
            .SetPermission(this.Permissions)
            .SetPictureUrl(this.PictureUrl!)
            .SetVersionDate(this.VersionDate);

        return result
    }

    LoadFromApiByCompany = (response: any): IUserModel => {
        const token = GetValue(response, "token"),
            userData = GetValue(response, "userData"),
            userCompanies: any[] = userData?.userCompanies/*.$values*/ || GetValue(userData, "userCompanies") || [],
            permissions = userCompanies.map(x => GetValue(x, "userPermission")),
            apiCreated: string = GetValue(userData, "Created"),
            apiModified: string = GetValue(userData, "Modified");;

        this.Id = GetValue(userData, "userId") as number;
        this._companies = userCompanies.map(x => new CompanyModel().LoadFromAPI(x));
        this._userCompanyPermissions = permissions.map(x => new UserCompanyPermissionModel().LoadFromAPI(x));
        this.Created = (apiCreated && IgnoreTimeZoneApiDate(apiCreated)) || this.Created;
        this.CreatedById = GetValue(userData, "createdById") as number;
        this.Modified = (apiModified && IgnoreTimeZoneApiDate(apiModified)) || this.Modified;
        this.IsActive = GetValue(userData, "isActive") as boolean;
        this.IsMaster = GetValue(userData, "isMaster") as boolean;
        this._token = token;

        this.LoadPermissions();

        return this;
    }

    LoadFromAPI = (response: any): IUserModel => {
        const apiCreated: string = GetValue(response, "Created"),
            apiVersionDate: string = GetValue(response, "VersionDate"),
            apiModified: string = GetValue(response, "Modified");

        this.Id = GetValue(response, "UserId") as number;
        this.Created = (apiCreated && IgnoreTimeZoneApiDate(apiCreated)) || this.Created;
        this.CreatedById = GetValue(response, "CreatedById") as number;
        this.VersionDate = (apiVersionDate && IgnoreTimeZoneApiDate(apiVersionDate)) || this.VersionDate;
        this.Email = GetValue(response, "Email") as string;
        this.IsActive = GetValue(response, "IsActive") as boolean;
        this.IsMaster = GetValue(response, "IsMaster") as boolean;
        this.Login = GetValue(response, "Login") as string;
        this.Modified = (apiModified && IgnoreTimeZoneApiDate(apiModified)) || this.Modified;
        this.Name = GetValue(response, "Name") as string;
        this.PictureUrl = GetValue(response, "PictureUrl") as string;

        const permissions: any[] = GetValue(response, "Permissions")?.$values || [];

        this._userCompanyPermissions = (GetValue(response, "Permissions")?.$values as any[])?.map(x => new UserCompanyPermissionModel().LoadFromCached(x)) || [];
        this.LoadPermissions();
        // this.Permissions =  permissions.map(permission => new UserCompanyPermissionModel().LoadFromAPI(permission));

        // this.Permissions = GetValue(response, "Permissions");//?.map(x => new UserCompanyPermissionModel().LoadFromAPI(x));

        return this;
    }

    LoadFromCached = (model: any): IUserModel => {

        this.LoadFromAPI(model);
        this.Id = GetValue(model, "Id") as number;
        this._companies = (GetValue(model, "_companies") as any[])?.map(x => new CompanyModel().LoadFromCached(x)) || [];
        this._userCompanyPermissions = (GetValue(model, "_userCompanyPermissions") as any[])?.map(x => new UserCompanyPermissionModel().LoadFromCached(x)) || [];
        this._token = GetValue(model, "_token") as string;

        this.LoadPermissions();

        return this;
    };

    GetToken = (): string | undefined => this._token;

    LoadPermissions = (): void => {

        this.Permissions = new Map();
        if (this._userCompanyPermissions != null && this._userCompanyPermissions.some(x => x)) {
            this.Permissions = this._userCompanyPermissions
                .filter(x => x != null && x.IsActive && x.CompanyId > 0 && x.Id > 0 && x.UserId == this.Id)
                .reduce((map, permission) => map.set(permission.CompanyId.toString(), permission), new Map<string, IUserCompanyPermissionModel>());
        }
    }

    LoadPremissionsFromCollection = (permissions: IUserCompanyPermissionModel[]): IUserModel => {
        this._userCompanyPermissions = permissions;
        this.LoadPermissions();
        return this;
    }

    ToJson = (): string => {
        const dto = {
            userId: this.Id,
            created: moment(this.Created).format(),
            createdById: this.CreatedById,
            versionDate: moment(this.VersionDate).format(),
            email: this.Email,
            isActive: this.IsActive,
            isMaster: this.IsMaster,
            login: this.Login,
            modified: moment(this.Modified).format(),
            name: this.Name,
            pictureUrl: this.PictureUrl
        },
            result = JSON.stringify(dto);
        return result;
    }
}